import { Spacing, Text } from '@vkontakte/vkui';
import { parse } from 'qs';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { PROMO_TARIFFS } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { autoUploadOnboarding, helpAutoUploadHref } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { historyPush } from 'reactApp/modules/router/router.module';
import { openAutoUploadOnboadingFinalDialog } from 'reactApp/sections/QuotaLanding/AutoUploadOnboardingFinalDialog/AutoUploadOnboardingFinalDialog.helpers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './AutoUploadOnboardingDialog.css';
import {
    type AutoUploadOnboardingData,
    autoUploadOnboardingData,
    autoUploadOnboardingGaName,
    autoUploadOnboardingMobileData,
} from './AutoUploadOnboardingDialog.data';
import { closeAutoUploadOnboadingDialog } from './AutoUploadOnboardingDialog.helpers';
import type { AutoUploadOnboardingDialogProps } from './AutoUploadOnboardingDialog.types';

const sendRadar = (action: 'click' | 'show' | 'close', data?: Record<string, string>) => {
    const variant = autoUploadOnboarding || '';
    const name = autoUploadOnboardingGaName[variant];

    sendPaymentGa({
        eventCategory: ECategoryGa.modal,
        action,
        ...(name && { name }),
        ...(data && { ...data }),
    });
};

export const AutoUploadOnboardingDialog = memo<AutoUploadOnboardingDialogProps>(({ onClose, onClick, onShow }) => {
    const dispatch = useDispatch();
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const scrollRef = useRef<HTMLDivElement>(null);
    const { pathname, search } = useLocation();

    const queryParams = parse(search, { ignoreQueryPrefix: true });
    const isFromAction = queryParams?.action === 'request-autoupload';
    const variant = isFromAction ? 'b' : autoUploadOnboarding;
    const dialogData = autoUploadOnboardingData[variant || ''] as AutoUploadOnboardingData;
    const dialogDataMobile = autoUploadOnboardingMobileData[variant || ''] as AutoUploadOnboardingData;

    useEffect(() => {
        onShow?.();
        sendRadar('show');
        emitAnalyticEvent(AnalyticEventNames.AUTOUPLOAD_ONBOARDING_SHOW);
    }, []);

    const handleClick = useCallback(() => {
        let button_name = '';

        if (variant === 'b') {
            const searchString = isFromAction ? search : `?${PaymentUTM.autoupload}`;
            dispatch(historyPush({ id: pathname, search: searchString, hash: 'price' }));
            button_name = 'chose_tarif';
        } else if (variant === 'c') {
            dispatch(
                openTariffBuy({
                    productId: PROMO_TARIFFS.autouploadPromoTariff,
                    checkAnonymous: false,
                    isMobile,
                    paySource: 'autoload_1tb',
                    title: !isMobile ? 'Тариф 1 ТБ и опция безлимит' : undefined,
                    hideOtherTariffs: true,
                })
            );
            button_name = 'buy_subscription';
        } else if (variant === 'd') {
            button_name = 'learn_more';
            openAutoUploadOnboadingFinalDialog();
        }
        sendRadar('click', { button_name });
        emitAnalyticEvent(AnalyticEventNames.AUTOUPLOAD_ONBOARDING_CLICK);
        closeAutoUploadOnboadingDialog();
        onClick?.();
    }, [dispatch, isMobile, variant, onClick, isFromAction, pathname, search]);

    const handleClose = useCallback(() => {
        onClose?.();
        sendRadar('close');
    }, [onClose]);

    const content = useMemo(() => {
        return (
            <>
                <Spacing size={16} />
                <Text className={styles.text}>{dialogData.text}</Text>
                {variant === 'c' && (
                    <a className={styles.link} href={helpAutoUploadHref} target="_blank" rel="noreferrer">
                        Подробные условия использования
                    </a>
                )}
                <Spacing size={36} />
            </>
        );
    }, [dialogData.text, variant]);

    if (isMobile) {
        return (
            <MobileDialog
                id="autoupload-onboarding-mobile"
                onClose={handleClose}
                topmost
                closeOnDimmerClick
                scrollRef={scrollRef}
                mod="base"
                contextPadding="zero"
                isRebrandingQuotaLanding
            >
                <div ref={scrollRef}>
                    <img src={dialogDataMobile.img} alt={dialogDataMobile.title} className={styles.mobileImg} />
                    <Spacing size={10} />
                    <Text className={styles.mobileTitle}>{dialogDataMobile.title}</Text>
                    <Spacing size={8} />
                    <Text className={styles.mobileText}>{dialogDataMobile.text}</Text>
                    {variant === 'c' && (
                        <a className={styles.mobileLink} href={helpAutoUploadHref} target="_blank" rel="noreferrer">
                            Подробные условия использования
                        </a>
                    )}
                    <Spacing size={24} />
                    <div className={styles.buttonWrapper}>
                        <Button primary theme="vk" sizeMode={ButtonSizeMode.extraBig} onClick={handleClick} className={styles.button}>
                            {dialogDataMobile.buttonText}
                        </Button>
                    </div>
                    <Spacing size={16} />
                </div>
            </MobileDialog>
        );
    }

    return (
        <WhatsNewDialog
            imageUrl={dialogData.img}
            title={dialogData.title}
            content={content}
            primaryButtonText={dialogData.buttonText}
            onClick={handleClick}
            onClose={handleClose}
            imageBgColor="var(--rebranding--dark_theme_color_background_sky)"
            buttonTheme="vk"
            dialogSize="tiny"
            qaId="autoupload-onboarding"
            isRebranding
        />
    );
});

AutoUploadOnboardingDialog.displayName = 'AutoUploadOnboardingDialog';
